import { yupResolver } from '@hookform/resolvers/yup';
import { Box, IconButton, SvgIcon, Typography } from '@mui/material';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  Button,
  ConfirmationMessageModal,
  TextFieldWrapperWithInputMask,
} from '@Components/index';
import MainSectionLayout from '@Components/MainSectionLayout';
import commonConstants from '@Constants/common.constants';
import Icons from '@Constants/Icons.constants';
import {
  StyledForm,
  StyledStack,
} from '@Containers/UserCallerIds/UserCallerIds.styles';
import { AddForwardingNumberInputs } from '@Models/callerIds.models';
import {
  useAddForwardingNumberMutation,
  useDeleteForwardingNumberByIdMutation,
  useGetForwardingNumberQuery,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import {
  formatNumber,
  getDateMMDDYYYYHHMM,
  unformatUSNumber,
} from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const ForwardingNumber: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [showAddForwardingNumberForm, setShowAddForwardingNumberForm] =
    useState(false);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [
    forwardingDeleteConfirmationMessage,
    setForwardingDeleteConfirmationMessage,
  ] = useState<React.ReactNode | null>(null);
  const [
    isForwardingDeleteConfirmationModalOpen,
    setIsForwardingDeleteConfirmationModalOpen,
  ] = useState(false);
  const [forwardingConfirmationModalData, setForwardingConfirmationModalData] =
    useState<string | undefined>();
  const [disableFormButtons, setDisableFormButtons] = useState(false);
  const [showAddButton, setShowAddButton] = useState(true);

  // add forwarding number
  const [addForwardingNumber, addForwardingNumberMethods] =
    useAddForwardingNumberMutation();

  // delete forwarding number
  const [deleteForwardingNumberById, deleteForwardingNumberByIdMethods] =
    useDeleteForwardingNumberByIdMutation();

  // load forwarding number entity
  const {
    data: dataOfForwardingNumber = undefined,
    isLoading: loadingForwardingNumber,
    isFetching: fetchingForwardingNumber,
    isError: isErrorForGetForwardingNumber,
    error: errorForGetForwardingNumber,
  } = useGetForwardingNumberQuery();

  useEffect(() => {
    setShowAddButton(true);
  }, [dataOfForwardingNumber]);

  const AddForwardingNumberSchema = (): yup.AnyObjectSchema => {
    const requiredMessage = t('common.formErrorMessages.required');

    return yup
      .object({
        forwardingNumber: yup.string().required(requiredMessage),
      })
      .required();
  };

  /**
   * Add Forwarding Number Form Methods: Handles form validation using useForm hook
   */
  const forwardingNumberMethods = useForm<AddForwardingNumberInputs>({
    resolver: yupResolver(AddForwardingNumberSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /**
   * Error Alerts for the Get Forwarding Number
   */
  useEffect(() => {
    if (isErrorForGetForwardingNumber && errorForGetForwardingNumber) {
      setDisableFormButtons(false);
      triggerAlert(errorForGetForwardingNumber, setAlertState);
    }
  }, [
    triggerAlert,
    errorForGetForwardingNumber,
    isErrorForGetForwardingNumber,
  ]);

  /**
   * Error and Success alerts for Add Forwarding Number
   */
  useEffect(() => {
    if (addForwardingNumberMethods.error) {
      setDisableFormButtons(false);
      triggerAlert(addForwardingNumberMethods.error, setAlertState);
    } else if (addForwardingNumberMethods.isSuccess) {
      setShowAddForwardingNumberForm(false);
      setDisableFormButtons(false);
    }
  }, [
    addForwardingNumberMethods.isSuccess,
    addForwardingNumberMethods.error,
    addForwardingNumberMethods.data,
    triggerAlert,
    forwardingNumberMethods,
    t,
  ]);

  /**
   * Error and Success alerts for Delete Forwarding Number
   */
  useEffect(() => {
    if (
      deleteForwardingNumberByIdMethods.isError &&
      deleteForwardingNumberByIdMethods.error
    ) {
      triggerAlert(deleteForwardingNumberByIdMethods.error, setAlertState);
    }
    if (deleteForwardingNumberByIdMethods.isSuccess) {
      triggerAlert(
        commonConstants.GLOBAL_MESSAGES.FORWARDING_NUMBER_DELETE,
        setAlertState
      );
    }
  }, [
    deleteForwardingNumberByIdMethods.isError,
    deleteForwardingNumberByIdMethods.isSuccess,
    deleteForwardingNumberByIdMethods.error,
    deleteForwardingNumberByIdMethods.data,
    triggerAlert,
  ]);

  const addForwardingNumberValue = () => {
    setShowAddForwardingNumberForm(true);
  };

  const resetForwardingNumberCallback = useCallback(() => {
    setShowAddForwardingNumberForm(false);
  }, []);

  const cancelAddForwardingNumber = () => {
    resetForwardingNumberCallback();
  };

  /** Forwarding Number Table Loading State */
  const loadingForwarding = loadingForwardingNumber || fetchingForwardingNumber;

  /**
   * Add New Forwarding Number Form Methods: Updates the form state on submit
   */
  const onForwardingNumberSubmitHandler: SubmitHandler<AddForwardingNumberInputs> =
    (formData) => {
      if (formData) {
        setShowAddButton(false);
        const formattedPhone = formData.forwardingNumber;
        const filteredDigits = formattedPhone.replace(/\D/g, '');
        let planePhone: string;
        if (filteredDigits.startsWith('0')) {
          planePhone = `+38${unformatUSNumber(formattedPhone)}`;
        } else {
          planePhone = `+1${unformatUSNumber(formattedPhone)}`;
        }

        if (
          filteredDigits.length === commonConstants.PHONE_NUMBER_DIGITS_COUNT
        ) {
          setDisableFormButtons(true);
          return addForwardingNumber({
            forwardingNumber: planePhone,
          });
        }

        setShowAddButton(true);
        forwardingNumberMethods.setError('forwardingNumber', {
          type: 'manual',
          message: t('userCallerIds.Invalid input'),
        });
      }
    };

  /**
   * Constructs the UI (row) of the forwarding number table
   * @param id unique id of the caller
   * @param forwardingNumber forwarding number of the caller
   * @param date createdAt date
   * @param deleteForwardingNumber callback method to delete the forwarding number by unique id
   * @returns a single row, populating the data
   */
  const ForwardingNumberTableRow = (
    id: string,
    forwardingNumber: string,
    date: string,
    deleteForwardingNumber: (id: string, forwardingNumber: string) => void
  ): JSX.Element => (
    <StyledStack key={id}>
      <Typography className="phone-number" variant="main">
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {formatNumber(forwardingNumber)}
      </Typography>
      <Typography className="date" variant="main">
        {t('userCallerIds.Created Date', {
          interpolation: {
            escapeValue: false,
          },
          date: getDateMMDDYYYYHHMM(new Date(date), true),
        })}
      </Typography>
      <IconButton
        onClick={() => deleteForwardingNumber(id, forwardingNumber)}
        className="delete-icon"
        aria-label="delete"
      >
        <SvgIcon>{Icons.delete}</SvgIcon>
      </IconButton>
    </StyledStack>
  );

  /**
   * Deletes the forwarding number
   * @param id Id of the caller
   * @param phoneNumber forwarding number
   */
  const toggleDeleteForwardingNumber = (id: string, phoneNumber: string) => {
    setForwardingConfirmationModalData(id);
    // Formatting phone to US / UA formats
    const phone = formatNumber(phoneNumber);

    const message: React.ReactNode = (
      <Typography
        variant="main"
        sx={{
          '& .phone': {
            color: 'secondary.main',
          },
        }}
      >
        <Trans
          i18nKey="common.confirmationMessages.Confirm Forwarding Delete"
          values={{ phone }}
          components={{ strong: <strong />, span: <span className="phone" /> }}
        />
      </Typography>
    );
    setForwardingDeleteConfirmationMessage(message);
    setIsForwardingDeleteConfirmationModalOpen(true);
  };

  /**
   * Hides the confirmation modal and deletes the forwarding number
   * @param id of callerId
   * @returns delete mutation
   */
  const deleteForwardingNumberActionHandler = (id: string | undefined) => {
    setForwardingConfirmationModalData(undefined);
    setIsForwardingDeleteConfirmationModalOpen(false);
    if (id) {
      return deleteForwardingNumberById(id);
    }
  };

  /**
   * Conditionally shows/hides the Add Forwarding Number Form
   */
  const AddForwardingNumberForm = (
    <Box sx={{ p: 0 }}>
      <Typography
        variant="main"
        fontSize="16px"
        fontWeight="bold"
        textAlign="left"
        paddingLeft={0}
        sx={{ my: 3.2 }}
      >
        {t('userCallerIds.Forwarding Number')}
      </Typography>
      {!showAddForwardingNumberForm &&
        !dataOfForwardingNumber &&
        showAddButton && (
          <Button
            onClick={() => addForwardingNumberValue()}
            color="secondary"
            variant="outlined"
          >
            {t('userCallerIds.Add Forwarding Number')}
          </Button>
        )}

      {/* Clicking on the "Add Forwarding Number" button will hide the button and show the form. */}
      {showAddForwardingNumberForm && (
        <>
          <Typography mb={1.6} variant="main">
            {t('userCallerIds.Forwarding Number Message')}
          </Typography>
          <FormProvider {...forwardingNumberMethods}>
            <StyledForm
              component="form"
              onSubmit={forwardingNumberMethods.handleSubmit(
                onForwardingNumberSubmitHandler
              )}
            >
              <Box display="flex">
                <TextFieldWrapperWithInputMask
                  name="forwardingNumber"
                  placeholder={`${t(
                    'userCallerIds.Enter forwarding number'
                  )} *`}
                  className="add-number-input"
                  mask={commonConstants.PHONE_NUMBER_INPUT_MASK}
                />
              </Box>
              <Box>
                <Button
                  variant="text"
                  type="button"
                  color="secondary"
                  sx={{ mr: 2.4 }}
                  disabled={loadingForwarding || disableFormButtons}
                  onClick={() => cancelAddForwardingNumber()}
                >
                  {t('common.confirmationModal.CANCEL')}
                </Button>
                <Button
                  type="submit"
                  disabled={loadingForwarding || disableFormButtons}
                >
                  {t('common.messages.add')}
                </Button>
              </Box>
            </StyledForm>
          </FormProvider>
        </>
      )}
    </Box>
  );

  /**
   * Forwarding number table component
   */
  const ForwardingNumberTableComponent = (
    <>
      <Box sx={{ overflowX: 'auto', minWidth: '100%', mb: 3 }}>
        {dataOfForwardingNumber &&
          ForwardingNumberTableRow(
            dataOfForwardingNumber.id,
            dataOfForwardingNumber.forwardingNumber,
            dataOfForwardingNumber.createdAt,
            toggleDeleteForwardingNumber
          )}
      </Box>
      {/* Delete Confirmation Modal */}
      <ConfirmationMessageModal
        isModalOpen={isForwardingDeleteConfirmationModalOpen}
        setIsModalOpen={setIsForwardingDeleteConfirmationModalOpen}
        message={forwardingDeleteConfirmationMessage}
        onClick={() =>
          deleteForwardingNumberActionHandler(forwardingConfirmationModalData)
        }
      />
    </>
  );

  return (
    <MainSectionLayout
      alertState={alertState}
      filters={AddForwardingNumberForm}
      table={ForwardingNumberTableComponent}
      tabChild
    />
  );
};

export default ForwardingNumber;
